/* Shared model types. All coordinates are inches, +x right, +y down. */

export interface Pt { x: number; y: number }

/** Screen-space rotated-rect hit target for click-to-edit labels (runtime only).
    x,y = center; ang = rotation; hw/hh = half width/height along the text. */
export interface Hit { x: number; y: number; ang: number; hw: number; hh: number }

export interface Wall {
  id: string;
  x1: number; y1: number;
  x2: number; y2: number;
  th: number;
  /** User locked this wall's length: never stretch it, only move it rigidly. */
  locked?: boolean;
  _hitLen?: Hit | null;
  _hitClear?: Hit | null;
}

export type OpeningType = 'door' | 'double' | 'bifold' | 'slider' | 'pocket' | 'cased' | 'window';

/** Catalog of opening types: panel/tool label, default width, shortcut key. */
export const OPENING_DEFS: Record<OpeningType, { label: string; width: number; key: string }> = {
  door: { label: 'Door', width: 30, key: 'D' },
  double: { label: 'Double door', width: 60, key: 'U' },
  bifold: { label: 'Bifold door', width: 48, key: 'I' },
  slider: { label: 'Sliding door', width: 60, key: 'B' },
  pocket: { label: 'Pocket door', width: 30, key: 'P' },
  cased: { label: 'Cased opening', width: 36, key: 'O' },
  window: { label: 'Window', width: 36, key: 'N' },
};

/** Door-family opening types: placed with the Door tool, picked in the panel.
    (Windows have their own tool.) */
export const DOOR_TYPES: OpeningType[] = (Object.keys(OPENING_DEFS) as OpeningType[]).filter(k => k !== 'window');

export interface Opening {
  id: string;
  type: OpeningType;
  wallId: string;
  /** Distance of the opening center from the wall's (x1,y1), along the wall. */
  t: number;
  width: number;
  /** door: hinge jamb (0 = low-t, 1 = high-t) · pocket: which jamb the cavity is behind. */
  hinge?: number;
  /** door/double/bifold: 1 = opens toward the wall's left normal (-uy, ux), -1 = other side. */
  swing?: number;
}

export interface Fixture {
  id: string;
  type: string;
  x: number; y: number;
  w: number; h: number;
  rot: number;
  label: string;
  shelves?: number;   // open-shelving units: number of shelf boards (3D view)
}

export interface LabelItem {
  id: string;
  x: number; y: number;
  text: string;
  size: number;
  _bb?: { x: number; y: number; w: number; h: number };
}

/** Signed perpendicular distance from wall a's centerline to wall b's midpoint. */
export interface DistConstraint { id: string; type: 'dist'; a: string; b: string; dist: number; _hit?: Hit | null }
/** Angle from wall a's direction to wall b's direction, degrees in [0,180). 90 = ⊥, 0 = ∥. */
export interface AngleConstraint {
  id: string; type: 'angle'; a: string; b: string; deg: number;
  _hit?: Hit | null;
  /** Second click target when the walls don't share a corner (badge on each wall). */
  _hit2?: Hit | null;
}
/** Wall a is horizontal or vertical. */
export interface AxisConstraint { id: string; type: 'axis'; a: string; axis: 'h' | 'v'; _hit?: Hit | null }
/** Walls a and b have equal length. */
export interface EqualConstraint { id: string; type: 'equal'; a: string; b: string; _hit?: Hit | null }
/** Coincident (T-junction): wall a's endpoint (end: 0 = x1y1, 1 = x2y2) stays
    ON wall b's centerline — it may slide along b, never leave it. Corner-to-
    corner coincidence needs no constraint: identical coords weld into one
    solver node automatically. */
export interface CoinConstraint { id: string; type: 'coin'; a: string; end: 0 | 1; b: string; _hit?: Hit | null }
/** Walls a and b lie on one straight line (parallel with zero offset). */
export interface ColinConstraint { id: string; type: 'colin'; a: string; b: string; _hit?: Hit | null; _hit2?: Hit | null }
/** Wall a's endpoint (end: 0/1) sits at the midpoint of wall b. */
export interface MidConstraint { id: string; type: 'mid'; a: string; end: 0 | 1; b: string; _hit?: Hit | null }
/** Wall a is grounded at the stored coordinates — it can never move.
    Delete the constraint to free it. */
export interface FixConstraint {
  id: string; type: 'fix'; a: string;
  x1: number; y1: number; x2: number; y2: number;
  _hit?: Hit | null;
}

export type Constraint = DistConstraint | AngleConstraint | AxisConstraint | EqualConstraint
  | CoinConstraint | ColinConstraint | MidConstraint | FixConstraint;

export interface Doc {
  version: number;
  walls: Wall[];
  openings: Opening[];
  fixtures: Fixture[];
  labels: LabelItem[];
  constraints: Constraint[];
}

export type ItemKind = 'walls' | 'openings' | 'fixtures' | 'labels';
